import changelog from '@/../../../../CHANGELOG.md?raw'
import {
    Card,
    CardContent,
    CardHeader,
    List,
    ListItem,
    ListItemText,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { useApplicationInformation } from '@/features/about/queries/useApplicationInformation'
import { lazyLoad } from '@/helper/lazyLoad'

const seleniumPrefix = 'about'

const LazyMarkdown = lazyLoad(() => import('react-markdown'), 'default')

const RouteComponent: React.FC = () => {
    const { tab } = Route.useSearch()
    const navigate = Route.useNavigate()

    const { t } = useTranslation()

    const { data: applicationInformation } = useApplicationInformation()

    return (
        <Card>
            <title>{t('about.pageTitle')}</title>

            <CardHeader title={t('about.pageTitle')} />

            <Tabs
                sx={{ marginLeft: 2 }}
                value={tab}
                onChange={(_, value: TabSchema) =>
                    navigate({
                        search: prev => ({ ...prev, tab: value }),
                        replace: true,
                    })
                }
            >
                <Tab
                    value={'versions'}
                    label={t('about.usedVersions')}
                    data-selenium={`${seleniumPrefix}-versions-tab`}
                />
                <Tab
                    value={'changelog'}
                    label={t('about.changelog')}
                    data-selenium={`${seleniumPrefix}-changelog-tab`}
                />
            </Tabs>

            {tab === 'versions' ?
                <CardContent>
                    <Typography variant={'h6'}>
                        {t('about.backendAssemblyVersions', {
                            commit: applicationInformation.commitHash,
                        })}
                    </Typography>
                    <List
                        disablePadding={true}
                        sx={{
                            '& .MuiListItemText-primary > span': {
                                cursor: 'help',
                            },
                        }}
                    >
                        {applicationInformation.assemblyInformation
                            // Only display our dlls in the list.
                            .filter(ai =>
                                ai.name.toLowerCase().startsWith('mogi.')
                            )
                            .map(ai => (
                                <ListItem key={ai.name} disablePadding={true}>
                                    <ListItemText
                                        primary={
                                            <>
                                                {ai.name} -{' '}
                                                <span
                                                    title={t(
                                                        'about.versionNumber'
                                                    )}
                                                >
                                                    {ai.fileVersion}
                                                </span>
                                            </>
                                        }
                                    />
                                </ListItem>
                            ))}
                    </List>

                    <Typography
                        variant={'h6'}
                        sx={{ marginTop: 2, marginBottom: 4 }}
                    >
                        {t('about.frontendVersion', {
                            commit: import.meta.env.VITE_FRONTEND_COMMIT_HASH,
                            version: import.meta.env.VITE_FRONTEND_VERSION,
                        })}
                    </Typography>

                    <Typography variant={'body2'} sx={{ textAlign: 'center' }}>
                        {t('about.madeWithLove')}
                    </Typography>
                </CardContent>
            :   <CardContent>
                    <React.Suspense>
                        <LazyMarkdown>{changelog}</LazyMarkdown>
                    </React.Suspense>
                </CardContent>
            }
        </Card>
    )
}

const tabSchema = z.enum(['versions', 'changelog'])
type TabSchema = z.infer<typeof tabSchema>

export const Route = createFileRoute('/__base-layout/about')({
    component: RouteComponent,

    validateSearch: z.object({ tab: z._default(tabSchema, 'versions') }),

    search: { middlewares: [stripSearchParams({ tab: 'versions' })] },
})
